import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { ApiError, api, onAuthLost } from '../api/client.js';
import type { Me } from '../api/types.js';
import { CommandPalette } from '../components/CommandPalette/CommandPalette.js';
import { EmptyState, ErrorBanner, Skeleton } from '../components/States/States.js';
import { Specialties } from '../pages/Specialties.js';
import { Delivery } from '../pages/Delivery.js';
import { Login } from '../pages/Login.js';
import { RegionDetail } from '../pages/RegionDetail.js';
import { RegionsOverview } from '../pages/RegionsOverview.js';
import { Settings } from '../pages/Settings.js';
import { Sidebar } from '../shell/Sidebar.js';
import { dataStore } from '../store/dataStore.js';
import { Alerts } from '../pages/Alerts.js';
import { Audit } from '../pages/Audit.js';
import { StoreProvider } from '../store/StoreProvider.js';
import { color } from '../theme/tokens.js';

/**
 * Authenticated routing (FR-003, FR-005, FR-020, FR-021, FR-023).
 *
 * The product routes: `/regions`, `/regions/:regionCode`, the four `/delivery` states, the three
 * `/audit` states, and `/settings`. The two remaining out-of-scope sidebar entries — Alerts and
 * Leaderboard — have no route registered, so FR-023's "not navigable by direct URL" holds even if
 * someone types the path: there is nothing to reach.
 *
 * Audit joined the routed set with spec 007. Its routes are always registered, but the page
 * renders an "unavailable" state while the server answers `AUDIT_DISABLED` (FR-AU38) — the switch
 * lives on the server, so flipping it needs no release.
 *
 * Delivery joined the routed set with spec 002. Its four states share one component and differ
 * only by their path parameter, which is what gives every leader, manager and recruiter a
 * shareable address (FR-D16) — the reason people search could be deferred (FR-D11).
 *
 * Client-side gating here is **UX, not enforcement**. Every endpoint refuses an unauthenticated
 * or under-privileged request on the server regardless of what the browser renders (FR-003,
 * FR-046, gate 8) — this only saves the user from staring at a page that would have refused
 * to fill in.
 *
 * A deactivated account gets its own state rather than a redirect to sign-in: telling someone
 * to sign in again when signing in cannot help them is a loop, and FR-005 specifies the
 * "Access revoked / contact admin" message instead.
 */

type AuthState =
  | { status: 'loading' }
  | { status: 'signed-in'; me: Me }
  /** `expired`: a session that WAS live ended mid-use, as opposed to never having existed. */
  | { status: 'signed-out'; expired?: boolean }
  | { status: 'revoked' }
  | { status: 'error'; message: string };

function useAuth() {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  /*
   * Whether a session is live right now — set when `/me` succeeds, cleared the moment it is lost
   * or given up. A ref rather than a read of `state`, because the listener below outlives every
   * render and must not be re-subscribed on each one.
   */
  const live = useRef(false);

  /*
   * A session that ends mid-use (FR-063's idle or absolute cap, or a revocation) is otherwise
   * noticed by nothing: `/me` runs once, so every page would fail on its own with an error banner
   * and a Retry that can only 401 again. `onAuthLost` hears it from any request at all.
   *
   * ⚠ **Only a LIVE session can expire.** The initial `/me` 401s for anyone arriving without a
   * cookie, and that fires this listener too — telling a person who never signed in that their
   * session expired is false, so that case is left to `load`'s own catch.
   *
   * ⚠ **The store is closed, exactly as on sign-out**: `localStorage` outlives the session that
   * filled it, and a book of business must not sit in a browser nobody is signed in to.
   */
  useEffect(
    () =>
      onAuthLost((err) => {
        if (!live.current) return;
        live.current = false;
        dataStore.close();
        setState(
          err.isDeactivated ? { status: 'revoked' } : { status: 'signed-out', expired: true },
        );
      }),
    [],
  );

  const load = useCallback(() => {
    live.current = false;
    setState({ status: 'loading' });
    api
      .me()
      .then((me) => {
        live.current = true;
        setState({ status: 'signed-in', me });
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.isDeactivated) {
          setState({ status: 'revoked' });
        } else if (err instanceof ApiError && err.isUnauthenticated) {
          setState({ status: 'signed-out' });
        } else {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Could not verify your session.',
          });
        }
      });
  }, []);

  useEffect(load, [load]);

  const signOut = useCallback(() => {
    // Leaving on purpose is not an expiry. Standing the listener down first means the logout's
    // own 401 — a session that had already lapsed — does not flash "your session expired".
    live.current = false;
    // The store persists to `localStorage`, which outlives this session. Someone else
    // signing in on this browser must not inherit a book of business.
    dataStore.close();
    void api.logout().finally(() => {
      window.location.href = '/login';
    });
  }, []);

  return { state, reload: load, signOut };
}

function AppShell({ me, onSignOut }: { me: Me; onSignOut: () => void }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();

  // The palette listens for ⌘K globally and announces itself through this event, so the
  // shortcut works from any page without every page wiring up a key handler.
  useEffect(() => {
    const open = () => setPaletteOpen(true);
    window.addEventListener('bob:open-palette', open);
    return () => window.removeEventListener('bob:open-palette', open);
  }, []);

  useEffect(() => setPaletteOpen(false), [location.pathname]);

  return (
    /*
     * The store is mounted here, not above `useAuth`, for two reasons it cannot do without: it is
     * namespaced by the signed-in address, and the background prefetch it starts would otherwise
     * be sixty requests fired before anyone is known to be signed in.
     */
    <StoreProvider owner={me.email}>
      <div style={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <Sidebar me={me} onSignOut={onSignOut} />

        <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route path="/regions" element={<RegionsOverview />} />
            {/* The ONLY page given the header's Search Account control (owner decision
              2026-08-28, narrowing FR-019). A result lands on a Region Detail with the client
              row already selected (FR-050), so the control sits where its destination is; on
              every other page it offered a jump out of the page you had just navigated to.
              `⌘K` still opens the palette from anywhere, which is what FR-049 requires —
              `<CommandPalette>` owns that listener, not the header. */}
            <Route
              path="/regions/:regionCode"
              element={<RegionDetail onOpenSearch={() => setPaletteOpen(true)} />}
            />
            <Route
              path="/settings"
              element={
                me.role === 'admin' ? (
                  <Settings me={me} />
                ) : (
                  // Presentation only — the server has already refused the data (FR-046).
                  <div style={{ padding: 30 }}>
                    <EmptyState
                      title="Administrator access required"
                      detail="Settings is limited to administrators. If you need access, ask an administrator to change your role."
                    />
                  </div>
                )
              }
            />
            {/* Delivery — four states, one component. An address naming an unknown or
              out-of-scope entity renders an explanation rather than redirecting to the
              unfiltered page, which would answer a question nobody asked (FR-D16). */}
            <Route path="/specialties" element={<Specialties />} />
            {/* A discipline is two path segments so it can be linked to and reopened (FR-S25). */}
            <Route path="/specialties/:professionKey/:specialtyKey" element={<Specialties />} />
            <Route path="/delivery" element={<Delivery />} />
            <Route path="/delivery/leader/:leaderKey" element={<Delivery />} />
            <Route path="/delivery/manager/:managerId" element={<Delivery />} />
            <Route path="/delivery/recruiter/:recruiterId" element={<Delivery />} />
            {/* Audit — three states, one component (spec 007, FR-AU14). */}
            {/* Alerts (spec 008). `?manager=<employee no>` pre-filters to a team, once. */}
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/audit/:auditId" element={<Audit />} />
            <Route path="/audit/:auditId/:runDate" element={<Audit />} />
            <Route path="/" element={<Navigate to="/regions" replace />} />
            {/* No route exists for Alerts or Leaderboard — FR-023 requires them to be
              unreachable by direct URL, not merely dimmed. */}
            <Route
              path="*"
              element={
                <div style={{ padding: 30 }}>
                  <EmptyState
                    title="Not found"
                    detail="That page is not part of this release."
                  />
                </div>
              }
            />
          </Routes>
        </main>

        {/* `owner` namespaces the account pins in `localStorage`, which outlives a session on
            a shared machine — the same reason the data store is namespaced. */}
        <CommandPalette
          open={paletteOpen}
          onClose={() => setPaletteOpen(false)}
          owner={me.email}
        />
      </div>
    </StoreProvider>
  );
}

export function AppRoutes() {
  const { state, reload, signOut } = useAuth();

  if (state.status === 'loading') {
    return (
      <div style={{ padding: 40, background: color.appBg, minHeight: '100vh' }}>
        <Skeleton height={64} />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div style={{ padding: 40, background: color.appBg, minHeight: '100vh' }}>
        <ErrorBanner message={state.message} onRetry={reload} />
      </div>
    );
  }

  if (state.status === 'revoked') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: color.appBg,
          padding: 24,
        }}
      >
        <div style={{ maxWidth: 480 }}>
          <EmptyState
            title="Access revoked / contact admin"
            detail="This account has been deactivated. Signing in again will not restore access — an administrator must reactivate it from Settings."
          />
        </div>
      </div>
    );
  }

  if (state.status === 'signed-out') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="*"
          element={
            <Navigate to={state.expired ? '/login?error=SESSION_EXPIRED' : '/login'} replace />
          }
        />
      </Routes>
    );
  }

  return <AppShell me={state.me} onSignOut={signOut} />;
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
